import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import type {
  AnnotationTask,
  AnnotationWorkspaceState,
  Utterance,
  Entity,
  PhotoLabels,
  PhotoType,
} from '../types/annotation';

// ─── Action Types ──────────────────────────────────────────────────────────────

type AnnotationAction =
  | { type: 'SET_TASK'; task: AnnotationTask }
  | { type: 'SELECT_UTTERANCE'; utteranceId: string | null }
  | { type: 'SET_ACTIVE_PANEL'; panel: 'transcript' | 'annotation' | 'photo' }
  | { type: 'UPDATE_UTTERANCE'; utteranceId: string; updates: Partial<Utterance> }
  | { type: 'ADD_UTTERANCE'; utterance: Utterance }
  | { type: 'REMOVE_UTTERANCE'; utteranceId: string }
  | { type: 'ADD_ENTITY'; utteranceId: string; entity: Entity }
  | { type: 'REMOVE_ENTITY'; utteranceId: string; entityId: string }
  | { type: 'UPDATE_ENTITY'; utteranceId: string; entityId: string; updates: Partial<Entity> }
  | { type: 'UPDATE_PHOTO_LABELS'; photoId: string; labels: PhotoLabels }
  | { type: 'UPDATE_PHOTO_TYPE'; photoId: string; photoType: PhotoType | null }
  | {
      type: 'SET_AUTOSAVE_STATUS';
      status: AnnotationWorkspaceState['autoSaveStatus'];
      savedAt?: Date;
    }
  | { type: 'SET_DIRTY'; dirty: boolean };

// ─── Initial State ─────────────────────────────────────────────────────────────

const initialState: AnnotationWorkspaceState = {
  task: null,
  selectedUtteranceId: null,
  activePanel: 'transcript',
  isDirty: false,
  autoSaveStatus: 'idle',
  autoSavedAt: null,
};

// ─── Reducer ───────────────────────────────────────────────────────────────────

function annotationReducer(
  state: AnnotationWorkspaceState,
  action: AnnotationAction,
): AnnotationWorkspaceState {
  switch (action.type) {
    case 'SET_TASK':
      return {
        ...state,
        task: action.task,
        selectedUtteranceId: null,
        isDirty: false,
        autoSaveStatus: 'idle',
        autoSavedAt: null,
      };

    case 'SELECT_UTTERANCE':
      return { ...state, selectedUtteranceId: action.utteranceId };

    case 'SET_ACTIVE_PANEL':
      return { ...state, activePanel: action.panel };

    case 'UPDATE_UTTERANCE': {
      if (!state.task) return state;
      return {
        ...state,
        isDirty: true,
        task: {
          ...state.task,
          utterances: state.task.utterances.map((u) =>
            u.utteranceId === action.utteranceId ? { ...u, ...action.updates } : u,
          ),
        },
      };
    }

    case 'ADD_UTTERANCE': {
      if (!state.task) return state;
      return {
        ...state,
        isDirty: true,
        task: {
          ...state.task,
          utterances: [...state.task.utterances, action.utterance].sort(
            (a, b) => a.sequenceIndex - b.sequenceIndex,
          ),
        },
      };
    }

    case 'REMOVE_UTTERANCE': {
      if (!state.task) return state;
      const nextSelectedId =
        state.selectedUtteranceId === action.utteranceId ? null : state.selectedUtteranceId;
      return {
        ...state,
        isDirty: true,
        selectedUtteranceId: nextSelectedId,
        task: {
          ...state.task,
          utterances: state.task.utterances.filter(
            (u) => u.utteranceId !== action.utteranceId,
          ),
        },
      };
    }

    case 'ADD_ENTITY': {
      if (!state.task) return state;
      return {
        ...state,
        isDirty: true,
        task: {
          ...state.task,
          utterances: state.task.utterances.map((u) =>
            u.utteranceId === action.utteranceId
              ? { ...u, entities: [...u.entities, action.entity] }
              : u,
          ),
        },
      };
    }

    case 'REMOVE_ENTITY': {
      if (!state.task) return state;
      return {
        ...state,
        isDirty: true,
        task: {
          ...state.task,
          utterances: state.task.utterances.map((u) =>
            u.utteranceId === action.utteranceId
              ? {
                  ...u,
                  entities: u.entities.filter((e) => e.entityId !== action.entityId),
                }
              : u,
          ),
        },
      };
    }

    case 'UPDATE_ENTITY': {
      if (!state.task) return state;
      return {
        ...state,
        isDirty: true,
        task: {
          ...state.task,
          utterances: state.task.utterances.map((u) =>
            u.utteranceId === action.utteranceId
              ? {
                  ...u,
                  entities: u.entities.map((e) =>
                    e.entityId === action.entityId ? { ...e, ...action.updates } : e,
                  ),
                }
              : u,
          ),
        },
      };
    }

    case 'UPDATE_PHOTO_LABELS': {
      if (!state.task) return state;
      return {
        ...state,
        isDirty: true,
        task: {
          ...state.task,
          photos: state.task.photos.map((p) =>
            p.photoId === action.photoId ? { ...p, labels: action.labels } : p,
          ),
        },
      };
    }

    case 'UPDATE_PHOTO_TYPE': {
      if (!state.task) return state;
      return {
        ...state,
        task: {
          ...state.task,
          photos: state.task.photos.map((p) =>
            p.photoId === action.photoId ? { ...p, photoType: action.photoType } : p,
          ),
        },
      };
    }

    case 'SET_AUTOSAVE_STATUS':
      return {
        ...state,
        autoSaveStatus: action.status,
        autoSavedAt: action.savedAt ?? state.autoSavedAt,
        isDirty: action.status === 'saved' ? false : state.isDirty,
      };

    case 'SET_DIRTY':
      return { ...state, isDirty: action.dirty };

    default:
      return state;
  }
}

// ─── Context Value Interface ───────────────────────────────────────────────────

interface AnnotationContextValue {
  state: AnnotationWorkspaceState;
  setTask: (task: AnnotationTask) => void;
  selectUtterance: (utteranceId: string | null) => void;
  setActivePanel: (panel: 'transcript' | 'annotation' | 'photo') => void;
  updateUtteranceLocal: (utteranceId: string, updates: Partial<Utterance>) => void;
  addUtteranceLocal: (utterance: Utterance) => void;
  removeUtteranceLocal: (utteranceId: string) => void;
  addEntityLocal: (utteranceId: string, entity: Entity) => void;
  removeEntityLocal: (utteranceId: string, entityId: string) => void;
  updateEntityLocal: (utteranceId: string, entityId: string, updates: Partial<Entity>) => void;
  updatePhotoLabelsLocal: (photoId: string, labels: PhotoLabels) => void;
  updatePhotoTypeLocal: (photoId: string, photoType: PhotoType | null) => void;
  setAutoSaveStatus: (
    status: AnnotationWorkspaceState['autoSaveStatus'],
    savedAt?: Date,
  ) => void;
  setDirty: (dirty: boolean) => void;
  selectedUtterance: Utterance | null;
}

// ─── Context Creation ──────────────────────────────────────────────────────────

export const AnnotationContext = createContext<AnnotationContextValue>({
  state: initialState,
  setTask: () => undefined,
  selectUtterance: () => undefined,
  setActivePanel: () => undefined,
  updateUtteranceLocal: () => undefined,
  addUtteranceLocal: () => undefined,
  removeUtteranceLocal: () => undefined,
  addEntityLocal: () => undefined,
  removeEntityLocal: () => undefined,
  updateEntityLocal: () => undefined,
  updatePhotoLabelsLocal: () => undefined,
  updatePhotoTypeLocal: () => undefined,
  setAutoSaveStatus: () => undefined,
  setDirty: () => undefined,
  selectedUtterance: null,
});

// ─── Provider ──────────────────────────────────────────────────────────────────

export const AnnotationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(annotationReducer, initialState);

  const setTask = useCallback((task: AnnotationTask) => {
    dispatch({ type: 'SET_TASK', task });
  }, []);

  const selectUtterance = useCallback((utteranceId: string | null) => {
    dispatch({ type: 'SELECT_UTTERANCE', utteranceId });
  }, []);

  const setActivePanel = useCallback(
    (panel: 'transcript' | 'annotation' | 'photo') => {
      dispatch({ type: 'SET_ACTIVE_PANEL', panel });
    },
    [],
  );

  const updateUtteranceLocal = useCallback(
    (utteranceId: string, updates: Partial<Utterance>) => {
      dispatch({ type: 'UPDATE_UTTERANCE', utteranceId, updates });
    },
    [],
  );

  const addUtteranceLocal = useCallback((utterance: Utterance) => {
    dispatch({ type: 'ADD_UTTERANCE', utterance });
  }, []);

  const removeUtteranceLocal = useCallback((utteranceId: string) => {
    dispatch({ type: 'REMOVE_UTTERANCE', utteranceId });
  }, []);

  const addEntityLocal = useCallback((utteranceId: string, entity: Entity) => {
    dispatch({ type: 'ADD_ENTITY', utteranceId, entity });
  }, []);

  const removeEntityLocal = useCallback((utteranceId: string, entityId: string) => {
    dispatch({ type: 'REMOVE_ENTITY', utteranceId, entityId });
  }, []);

  const updateEntityLocal = useCallback(
    (utteranceId: string, entityId: string, updates: Partial<Entity>) => {
      dispatch({ type: 'UPDATE_ENTITY', utteranceId, entityId, updates });
    },
    [],
  );

  const updatePhotoLabelsLocal = useCallback((photoId: string, labels: PhotoLabels) => {
    dispatch({ type: 'UPDATE_PHOTO_LABELS', photoId, labels });
  }, []);

  const updatePhotoTypeLocal = useCallback((photoId: string, photoType: PhotoType | null) => {
    dispatch({ type: 'UPDATE_PHOTO_TYPE', photoId, photoType });
  }, []);

  const setAutoSaveStatus = useCallback(
    (status: AnnotationWorkspaceState['autoSaveStatus'], savedAt?: Date) => {
      dispatch({ type: 'SET_AUTOSAVE_STATUS', status, savedAt });
    },
    [],
  );

  const setDirty = useCallback((dirty: boolean) => {
    dispatch({ type: 'SET_DIRTY', dirty });
  }, []);

  const selectedUtterance = useMemo<Utterance | null>(() => {
    if (!state.task || !state.selectedUtteranceId) return null;
    return (
      state.task.utterances.find((u) => u.utteranceId === state.selectedUtteranceId) ?? null
    );
  }, [state.task, state.selectedUtteranceId]);

  const value = useMemo<AnnotationContextValue>(
    () => ({
      state,
      setTask,
      selectUtterance,
      setActivePanel,
      updateUtteranceLocal,
      addUtteranceLocal,
      removeUtteranceLocal,
      addEntityLocal,
      removeEntityLocal,
      updateEntityLocal,
      updatePhotoLabelsLocal,
      updatePhotoTypeLocal,
      setAutoSaveStatus,
      setDirty,
      selectedUtterance,
    }),
    [
      state,
      setTask,
      selectUtterance,
      setActivePanel,
      updateUtteranceLocal,
      addUtteranceLocal,
      removeUtteranceLocal,
      addEntityLocal,
      removeEntityLocal,
      updateEntityLocal,
      updatePhotoLabelsLocal,
      updatePhotoTypeLocal,
      setAutoSaveStatus,
      setDirty,
      selectedUtterance,
    ],
  );

  return <AnnotationContext.Provider value={value}>{children}</AnnotationContext.Provider>;
};

// ─── Hook ──────────────────────────────────────────────────────────────────────

export const useAnnotation = () => useContext(AnnotationContext);
